import { useState, useEffect, useRef } from 'react';
import { usersAPI, teamsAPI, seriesAPI } from '../../services/api';
import { PageHeader, StatCards, Field, Modal, ConfirmDialog, Spinner, EmptyState } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'admin',     label: 'Admin',     color: 'var(--red)',    desc: 'Full access to all features' },
  { value: 'scheduler', label: 'Scheduler', color: 'var(--blue)',   desc: 'Can create and edit schedules' },
  { value: 'team_user', label: 'Team User', color: 'var(--accent)', desc: 'Can view and manage own team' },
  { value: 'viewer',    label: 'Viewer',    color: 'var(--mid)',    desc: 'Read-only access' },
];
const STATUSES = ['active', 'pending', 'suspended'];
const ROLE_COLOR = { admin: 'var(--red)', scheduler: 'var(--blue)', team_user: 'var(--accent)', viewer: 'var(--mid)' };
const STATUS_COLOR = { active: 'var(--accent)', pending: 'var(--gold)', suspended: 'var(--red)' };
const EMPTY_FORM = { full_name: '', email: '', role: 'team_user', status: 'active', team_id: '', team_name_input: '', series_id: '', password: '' };

function RoleBadge({ role }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      background: `color-mix(in srgb, ${ROLE_COLOR[role] || 'var(--mid)'} 15%, transparent)`,
      color: ROLE_COLOR[role] || 'var(--mid)', textTransform: 'capitalize'
    }}>{role?.replace('_', ' ') || '—'}</span>
  );
}

function StatusDot({ status }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_COLOR[status] || 'var(--dim)', display: 'inline-block' }} />
      <span style={{ fontSize: 12, color: STATUS_COLOR[status] || 'var(--dim)', textTransform: 'capitalize' }}>{status}</span>
    </span>
  );
}

// Autocomplete team picker
function TeamAutocomplete({ teams, value, inputValue, onSelect, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(inputValue || '');
  const ref = useRef(null);

  useEffect(() => { setQuery(inputValue || ''); }, [inputValue]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.length > 0
    ? teams.filter(t => t.name.toLowerCase().includes(query.toLowerCase()) || (t.short_name || '').toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : [];

  const handleInput = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    if (!e.target.value) onClear();
  };

  const handleSelect = (team) => {
    setQuery(team.name);
    setOpen(false);
    onSelect(team);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="input"
          value={query}
          onChange={handleInput}
          onFocus={() => query && setOpen(true)}
          placeholder="Type team name…"
          style={{ flex: 1 }}
          autoComplete="off"
        />
        {value && (
          <button type="button" onClick={() => { setQuery(''); onClear(); }}
            className="btn btn-ghost" style={{ fontSize: 12, padding: '0 10px', color: 'var(--red)' }}>✕</button>
        )}
      </div>
      {value && (
        <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>✓ Team assigned</div>
      )}
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.18)', marginTop: 4, overflow: 'hidden'
        }}>
          {filtered.map(t => (
            <div key={t.id} onMouseDown={() => handleSelect(t)}
              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span>{t.name}</span>
              {t.short_name && <span style={{ fontSize: 11, color: 'var(--mid)', fontFamily: "'Roboto Mono', monospace" }}>{t.short_name}</span>}
            </div>
          ))}
        </div>
      )}
      {open && query.length > 1 && filtered.length === 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '10px 14px', fontSize: 12, color: 'var(--dim)', marginTop: 4
        }}>No teams match "{query}"</div>
      )}
    </div>
  );
}

function PermissionList({ role }) {
  const perms = {
    admin:     ['Manage users & roles', 'Create / edit all series & divisions', 'Generate & publish schedules', 'Edit ground availability', 'CricClubs sync (pull & push)', 'View all data'],
    scheduler: ['Generate & edit schedules', 'Edit ground availability', 'CricClubs pull sync', 'View all teams & fixtures'],
    team_user: ['View own team fixtures', 'Submit availability', 'Update team contact info'],
    viewer:    ['View schedules & standings', 'View team information'],
  };
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: 'var(--mid)', lineHeight: 1.9 }}>
      {(perms[role] || []).map(p => <li key={p}>{p}</li>)}
    </ul>
  );
}

export default function UsersPage() {
  const { isAdmin, user: me } = useAuthStore();
  const [users, setUsers]     = useState([]);
  const [teams, setTeams]     = useState([]);
  const [series, setSeries]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');
  const [filterRole, setFilterRole]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm]         = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeTab, setActiveTab]       = useState('users');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [u, t, s] = await Promise.all([usersAPI.list(), teamsAPI.list({}), seriesAPI.list()]);
      setUsers(u.data); setTeams(t.data); setSeries(s.data);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openEdit = (u) => {
    setForm({
      full_name: u.full_name || '',
      email: u.email || '',
      role: u.role || 'team_user',
      status: u.status || 'active',
      team_id: u.team_id || '',
      team_name_input: u.team_name || '',
      series_id: u.series_id || '',
      password: '',
    });
    setEditingId(u.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.full_name.trim()) return toast.error('Name is required');
    if (!form.email.trim()) return toast.error('Email is required');
    if (!editingId && !form.password) return toast.error('Password is required for new users');
    setSaving(true);
    try {
      const safeUUID = (v) => (v && v !== 'null' && v !== '' ? v : null);
      const payload = {
        full_name: form.full_name,
        email: form.email,
        role: form.role,
        status: form.status,
        team_id: safeUUID(form.team_id),
        series_id: safeUUID(form.series_id),
      };
      if (form.password) payload.password = form.password;
      if (editingId) {
        await usersAPI.update(editingId, payload);
      } else {
        await usersAPI.create(payload);
      }
      toast.success(editingId ? 'User saved' : 'User created');
      setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const approve = async (id) => {
    try { await usersAPI.approve(id); toast.success('User approved'); load(); }
    catch { toast.error('Failed to approve'); }
  };

  const deleteUser = async () => {
    try { await usersAPI.delete(deleteTarget); toast.success('User removed'); setDeleteTarget(null); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const filtered = users.filter(u => {
    if (filterRole && u.role !== filterRole) return false;
    if (filterStatus && u.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.team_name?.toLowerCase().includes(q);
    }
    return true;
  });

  const pending = users.filter(u => u.status === 'pending');
  const cards = [
    { label: 'Total Users',      value: users.length,   icon: '👤', color: 'var(--accent)' },
    { label: 'Active',           value: users.filter(u => u.status === 'active').length, icon: '✓', color: 'var(--accent)' },
    { label: 'Pending Approval', value: pending.length, icon: '⏳', color: pending.length > 0 ? 'var(--gold)' : 'var(--dim)' },
    { label: 'Admins',           value: users.filter(u => u.role === 'admin').length, icon: '★', color: 'var(--red)' },
  ];

  return (
    <div>
      <PageHeader icon="👤" title="Users & Roles"
        subtitle="Manage portal access, roles and team assignments"
        actions={isAdmin() && <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}>+ Invite User</button>}
      />
      <StatCards cards={cards} />

      {pending.length > 0 && (
        <div style={{ marginBottom: 20, padding: '12px 16px', background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.3)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <span style={{ flex: 1, fontSize: 13 }}><strong>{pending.length} user{pending.length > 1 ? 's' : ''}</strong> waiting for approval</span>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setFilterStatus('pending'); setActiveTab('users'); }}>View pending</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {[['users', `Users (${users.length})`], ['roles', 'Role Reference']].map(([t, label]) => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: activeTab === t ? 700 : 500,
            color: activeTab === t ? 'var(--accent)' : 'var(--mid)',
            borderBottom: activeTab === t ? '2px solid var(--accent)' : '2px solid transparent',
            fontSize: 13, marginBottom: -1
          }}>{label}</button>
        ))}
      </div>

      {activeTab === 'users' && (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '14px 20px', display: 'flex', gap: 10, alignItems: 'center', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <input className="input" placeholder="Search name, email, team…" value={search}
              onChange={e => setSearch(e.target.value)} style={{ flex: '1 1 200px', minWidth: 160 }} />
            <select className="input select" value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ width: 140 }}>
              <option value="">All roles</option>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select className="input select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 130 }}>
              <option value="">All statuses</option>
              {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>)}
            </select>
            {(search || filterRole || filterStatus) && (
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setSearch(''); setFilterRole(''); setFilterStatus(''); }}>✕ Clear</button>
            )}
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
          ) : filtered.length === 0 ? (
            <EmptyState icon="👤" title="No users found" description="Try adjusting your filters" />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th><th>Role</th><th>Status</th><th>Team</th><th>Last Login</th>
                    {isAdmin() && <th style={{ width: 140 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} style={{ opacity: u.status === 'suspended' ? 0.5 : 1 }}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{u.full_name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--mid)' }}>{u.email}</div>
                        {u.id === me?.id && <span style={{ fontSize: 10, color: 'var(--accent)' }}>● you</span>}
                      </td>
                      <td><RoleBadge role={u.role} /></td>
                      <td><StatusDot status={u.status} /></td>
                      <td style={{ fontSize: 12, color: 'var(--mid)' }}>
                        {u.team_name ? `${u.team_name}${u.team_short ? ` (${u.team_short})` : ''}` : '—'}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--dim)' }}>
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      {isAdmin() && (
                        <td>
                          <div style={{ display: 'flex', gap: 5 }}>
                            {u.status === 'pending' && (
                              <button className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: 'var(--accent)' }}
                                onClick={() => approve(u.id)}>✓ Approve</button>
                            )}
                            <button className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                              onClick={() => openEdit(u)}>Edit</button>
                            {u.id !== me?.id && (
                              <button className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: 'var(--red)' }}
                                onClick={() => setDeleteTarget(u.id)}>✕</button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'roles' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {ROLES.map(r => (
            <div key={r.value} className="card" style={{ padding: 20, borderTop: `3px solid ${r.color}` }}>
              <div style={{ marginBottom: 8 }}><RoleBadge role={r.value} /></div>
              <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 14 }}>{r.desc}</div>
              <div style={{ fontSize: 11, color: 'var(--dim)', marginBottom: 12 }}>
                {users.filter(u => u.role === r.value).length} user{users.filter(u => u.role === r.value).length !== 1 ? 's' : ''} assigned
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <PermissionList role={r.value} />
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <Modal title={editingId ? 'Edit User' : 'Invite User'} onClose={() => setShowForm(false)} width={500}>
          <Field label="Full Name" required>
            <input className="input" value={form.full_name} onChange={e => sf('full_name', e.target.value)} placeholder="e.g. Raj Patel" />
          </Field>
          <Field label="Email" required>
            <input className="input" type="email" value={form.email} onChange={e => sf('email', e.target.value)} placeholder="e.g. raj@team.com" />
          </Field>
          <Field label="Role">
            <select className="input select" value={form.role} onChange={e => sf('role', e.target.value)}>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className="input select" value={form.status} onChange={e => sf('status', e.target.value)}>
              {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>)}
            </select>
          </Field>
          <Field label="Assigned Team" note={form.role === 'team_user' ? 'Team user will only manage this team' : 'Optional — link user to a team'}>
            <TeamAutocomplete
              teams={teams}
              value={form.team_id}
              inputValue={form.team_name_input}
              onSelect={(team) => setForm(p => ({ ...p, team_id: team.id, team_name_input: team.name }))}
              onClear={() => setForm(p => ({ ...p, team_id: '', team_name_input: '' }))}
            />
          </Field>
          {form.role === 'scheduler' && (
            <Field label="Series Access" note="Leave blank for access to all series">
              <select className="input select" value={form.series_id} onChange={e => sf('series_id', e.target.value)}>
                <option value="">All series</option>
                {series.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Password" note={editingId ? 'Leave blank to keep existing password' : undefined} required={!editingId}>
            <input className="input" type="password" value={form.password} onChange={e => sf('password', e.target.value)} placeholder="••••••••" autoComplete="new-password" />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? <><Spinner size={14} /> Saving…</> : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          message="Remove this user? They will lose all portal access."
          danger onConfirm={deleteUser} onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
